(ns app.front.view.group.admin.import.workspace
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.import.model :as import-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.front.entities :as-alias entities]
            [app.front.view.group.admin.import.common :as import-session-common]
            [app.front.view.group.admin.import.subs :as import-subs]
            [core.front.event :refer [<sub]]
            [core.front.href :as href]
            [core.user.model :as user-model]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::selectable-workspace-statuses
  :<- [::import-session-common/root-config]
  (fn [root-config]
    (import-model/allowed-workspace-statuses (:group.config/target root-config))))

(rf/reg-sub ::existing-mapping-keys
  (fn [[_ session-id]]
    (rf/subscribe [::import-subs/current-mappings session-id]))
  (fn [mappings [_ _ target]]
    (->> (p/find-first mappings {:import-session-mapping/target target})
         :import-session-mapping/settings
         keys
         set)))

(rf/reg-sub ::selectable-work-types
  (fn [[_ session-id]]
    [(rf/subscribe [::import-session-common/selectable-categories])
     (rf/subscribe [::existing-mapping-keys session-id :workspace/category])
     (rf/subscribe [::existing-mapping-keys session-id :licence/category-group-config])])
  (fn [[categories ws-cat-ids licence-cat-ids] _]
    (let [mapped-cat-ids (if (seq licence-cat-ids)
                           licence-cat-ids
                           ws-cat-ids)]
      (->> categories
           (filter (comp mapped-cat-ids :db/id))
           (mapcat :entity/_parent)))))

(rf/reg-sub ::selectable-work-type-names
  (fn [[_ session-id]]
    [(rf/subscribe [::selectable-work-types session-id])
     (rf/subscribe [::existing-mapping-keys session-id :application/work-type])
     (rf/subscribe [:language])])
  (fn [[work-types mapped-work-type-names language]]
    (->> work-types
         (map #(config-model/localized-name % language))
         ;; Add anything that might has been mapped even if not currently valid
         (concat mapped-work-type-names)
         distinct
         sort)))

(rf/reg-sub ::selectable-work-target-names
  (fn [[_ session-id]]
    [(rf/subscribe [::selectable-work-types session-id])
     (rf/subscribe [::existing-mapping-keys session-id :application/work-target])
     (rf/subscribe [:language])])
  (fn [[work-types mapped-work-target-names language] _]
    (->> work-types
         (mapcat :entity/_parent)
         (map #(config-model/localized-name % language))
         ;; Add anything that might has been mapped even if not currently valid
         (concat mapped-work-target-names)
         distinct
         sort)))

(rf/reg-sub ::domain-group-property-unit-designators
  (fn [[_ session-id]]
    (rf/subscribe [::entities/entity session-id]))
  (fn [session _]
    (let [authority-group (:domain/authority-group session)
          domain-group    (or (:entity/parent authority-group)
                              authority-group)]
      (:property-unit-designator/_group domain-group))))

(rf/reg-sub
  ::selectable-property-unit-designators
  (fn [[_ session-id _]]
    [(rf/subscribe [::domain-group-property-unit-designators session-id])
     (rf/subscribe [:language])])
  (fn [[designators lang] [_ _ settings]]
    (let [designators    (or designators [])
          designator-ids (->> designators
                              (map :db/id)
                              set)]
      (->> (concat designators
                   (for [[designator-id designator-name] settings
                         :when                           (not (designator-ids designator-id))]
                     {:db/id                          designator-id
                      :property-unit-designator/names {:en designator-name}}))
           (sort-by (juxt #(or (property-model/unit-designator-name % lang) "")
                          (comp str :db/id)))))))


(defmethod import-session-common/entities-cols :import-session.type/workspace [_]
  [[:workspace/municipal-case-id
    :workspace/municipal-case-id
    {}]
   [:workspace/address
    :workspace/address]])


(defmethod import-session-common/entity-href :import-session.type/workspace [_ ws]
  (href/href :view/workspace {:workspace-id (:workspace/id ws)}))

(def property-unit-designator-settings-list
  (import-session-common/string-to-value-mapping-settings :data-import.mapping/property-unit-designator
                                                          ::selectable-property-unit-designators
                                                          :db/id
                                                          property-model/unit-designator-name))

(defn property-unit-designator-settings [{:keys [session-id settings] :as props}]
  (let [lang             (<sub [:language])
        designators      (<sub [::selectable-property-unit-designators session-id settings])
        default-settings (into {}
                               (map (juxt :db/id
                                          #(property-model/unit-designator-name % lang)))
                               designators)]
    [property-unit-designator-settings-list (assoc props :settings (merge default-settings settings))]))

(def workspace-status-settings
  (import-session-common/string-to-value-mapping-settings :data-import.mapping/workspace-status
                                                          ::selectable-workspace-statuses
                                                          identity
                                                          identity))

(def category-settings
  (import-session-common/string-to-value-mapping-settings :workspace/category
                                                          ::import-session-common/selectable-categories
                                                          :db/id
                                                          config-model/localized-name))

(def work-type-settings
  (import-session-common/string-to-value-mapping-settings :application/work-type
                                                          ::selectable-work-type-names
                                                          identity
                                                          identity))
(def work-target-settings
  (import-session-common/string-to-value-mapping-settings :application/work-target
                                                          ::selectable-work-target-names
                                                          identity
                                                          identity))

(defmethod import-session-common/mapping-settings :data-import.mapping/property-unit-designator [props]
  [property-unit-designator-settings props])

(defmethod import-session-common/mapping-settings :data-import.mapping/workspace-status [props]
  [workspace-status-settings props])

(defmethod import-session-common/mapping-settings :workspace/category [props]
  [category-settings props])

(defmethod import-session-common/mapping-settings :application/work-type [props]
  [work-type-settings props])

(defmethod import-session-common/mapping-settings :application/work-target [props]
  [work-target-settings props])

(def assignee-settings
  (import-session-common/string-to-value-mapping-settings :workspace/assignee
                                                          ::import-session-common/users
                                                          :db/id
                                                          (fn [user _] (user-model/full-name user))))

(defmethod import-session-common/mapping-settings :workspace/assignee [props]
  [assignee-settings props])

(def creator-settings
  (import-session-common/string-to-value-mapping-settings :data-import.mapping/creator
                                                          ::import-session-common/users
                                                          :db/id
                                                          (fn [user _] (user-model/full-name user))))

(defmethod import-session-common/mapping-settings :data-import.mapping/creator [props]
  [creator-settings props])


(def municipality-mapping-settings
  (import-session-common/string-to-value-mapping-settings
    :workspace/municipality
    ::import-session-common/selectable-municipalities
    :db/id
    municipality-model/municipality-name))


(defmethod import-session-common/mapping-settings :workspace/municipality [props]
  [municipality-mapping-settings props])
